import { action, computed, makeObservable, observable } from 'mobx';

import { ViewSettings } from 'src/api/chart/drilling-plan-charts-api';
import type { RigsChartViewTypes } from 'src/api/chart/rigs-chart-api';

import { ViewSettingsHelper } from '../../shared/view-settings-helper';

type TRigsChartViewWithProcessedSettings = Omit<RigsChartViewTypes.RigsChartView, 'settings'> & {
  settings: ViewSettings.TypedSettingsControls;
};

const DEFAULT_INFO_SETTINGS_VALUE: ViewSettings.InfoSettingsValues = {
  colorTab: {
    type: ViewSettings.ViewSettingsTabsDataTypes.grouped,
    settings: [
      {
        items: [
          {
            fieldId: 'cluster',
            isShown: true,
          },
          {
            fieldId: 'blockId',
            isShown: false,
          },
          {
            fieldId: 'fieldId',
            isShown: false,
          },
          {
            fieldId: 'subsoilUser',
            isShown: false,
          },
          {
            fieldId: 'wellPurposeId',
            isShown: false,
          },
          {
            fieldId: 'fluidTypeId',
            isShown: false,
          },
        ],
        fieldId: 'userSettings',
      },
      {
        items: [
          {
            fieldId: 'padReadiness',
            isShown: false,
          },
          {
            fieldId: 'padDocumentationReadiness',
            isShown: false,
          },
          {
            fieldId: 'routeReadiness',
            isShown: false,
          },
          {
            fieldId: 'routeDocumentationReadiness',
            isShown: false,
          },
          {
            fieldId: 'electricityReadiness',
            isShown: false,
          },
          {
            fieldId: 'routeType',
            isShown: false,
          },
        ],
        fieldId: 'systemSettings',
      },
    ],
  },
};

// TODO: костыль для обхода необходимости изменения вьюхи
const injectAdditionalWellSettings = (
  settings: ViewSettings.TypedSettingsControls,
): ViewSettings.TypedSettingsControls => {
  const wellSettings = { ...settings['wellTab'] };
  if (!wellSettings) {
    return settings;
  }

  const wellDataItems = [
    {
      control: 'VisibleRadioButton',
      fieldId: 'passingValue',
      type: 'Boolean',
      attrName: 'GOplan_GeologicalTask.passingValue',
      numberOfDecimalPlaces: 0,
    },
    {
      control: 'VisibleRadioButton',
      fieldId: 'commercialSpeed',
      type: 'Boolean',
      attrName: 'GOplan_GeologicalTask.commercialSpeed',
      numberOfDecimalPlaces: 0,
    },
    {
      control: 'VisibleRadioButton',
      fieldId: 'speedPerDay',
      type: 'Boolean',
      attrName: 'GOplan_GeologicalTask.speedPerDay',
      numberOfDecimalPlaces: 0,
    },
    {
      control: 'VisibleRadioButton',
      fieldId: 'wellType',
      type: 'Boolean',
      refObjectAttr: 'abbreviatedName',
      refObjectType: 'Common_WellType',
      attrName: 'GOplan_GeologicalTask.wellTypeId',
    },
    {
      control: 'VisibleRadioButton',
      fieldId: 'pilotNumber',
      type: 'Boolean',
      attrName: 'GOplan_GeologicalTask.pilotNumber',
    },
    {
      control: 'VisibleRadioButton',
      fieldId: 'miniPilotNumber',
      type: 'Boolean',
      attrName: 'GOplan_GeologicalTask.miniPilotNumber',
    },
    {
      control: 'VisibleRadioButton',
      fieldId: 'endingType',
      type: 'Boolean',
      refObjectAttr: 'abbreviatedName',
      refObjectType: 'Common_WellEndingType',
      attrName: 'GOplan_GeologicalTask.wellEndingTypeId',
    },
    {
      control: 'VisibleRadioButton',
      fieldId: 'reservoir',
      type: 'Boolean',
      attrName: 'GOplan_GeologicalTask.reservoirIds',
      refObjectAttr: 'name',
      refObjectType: 'Common_Reservoir',
    },
  ];

  const geologicalLoadItems = [
    {
      control: 'VisibleRadioButton',
      fieldId: 'coreSamplingInterval',
      type: 'Boolean',
      attrName: 'GOplan_GeologicalTask.coreSamplingIntervalId',
      refObjectAttr: 'prefix',
      refObjectType: 'Common_CoreSamplingInterval',
    },
    {
      control: 'VisibleRadioButton',
      fieldId: 'extractionVolume',
      type: 'Boolean',
      attrName: 'GOplan_GeologicalTask.coreSamplingIntervalId',
      numberOfDecimalPlaces: 2,
      refObjectAttr: 'extractionVolume',
      refObjectType: 'Common_CoreSamplingInterval',
    },
    {
      control: 'VisibleRadioButton',
      fieldId: 'testingFormationWorkAmount',
      type: 'Boolean',
      attrName: 'GOplan_GeologicalTask.testingFormationWorkAmount',
      numberOfDecimalPlaces: 0,
    },
  ];

  const newSettings: ViewSettings.TypedGroupedSettingsControls = {
    type: ViewSettings.ViewSettingsTabsDataTypes.grouped,
    fieldId: wellSettings.fieldId,
    order: wellSettings.order,
    groups: [
      {
        fieldId: 'wellData',
        items: wellDataItems,
      },
      {
        fieldId: 'geologicalLoad',
        items: geologicalLoadItems,
      },
    ],
  };

  return {
    ...settings,
    wellTab: newSettings,
  };
};

// TODO: костыль для обхода необходимости изменения вьюхи
const injectAdditionalPadSettings = (
  settings: ViewSettings.TypedSettingsControls,
): ViewSettings.TypedSettingsControls => {
  const padSettings = { ...settings['padTab'] };

  if (!padSettings || padSettings.type === ViewSettings.ViewSettingsTabsDataTypes.grouped) {
    return settings;
  }

  const newSettings: ViewSettings.TypedGroupedSettingsControls = {
    type: ViewSettings.ViewSettingsTabsDataTypes.grouped,
    fieldId: padSettings.fieldId,
    order: padSettings.order,
    groups: [
      {
        fieldId: 'padData',
        items: padSettings.items,
      },
      {
        maxElements: 5,
        fieldId: 'rsePreparations',
        items: [
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'padReadiness',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'padDocumentationReadiness',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'routeReadiness',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'routeDocumentationReadiness',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'electricityReadiness',
          },
        ],
      },
    ],
  };

  return {
    ...settings,
    padTab: newSettings,
  };
};

const injectAdditionalColorSettings = (
  settings: ViewSettings.TypedSettingsControls,
): ViewSettings.TypedSettingsControls => {
  const colorSettings = { ...settings['colorTab'] };

  if (!colorSettings || colorSettings.type === ViewSettings.ViewSettingsTabsDataTypes.grouped) {
    return settings;
  }

  const newSettings: ViewSettings.TypedGroupedSettingsControls = {
    type: ViewSettings.ViewSettingsTabsDataTypes.grouped,
    fieldId: 'colorTab',
    order: 3,
    groups: [
      {
        maxElements: 1,
        fieldId: 'userSettings',
        items: [
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'cluster',
            refObjectType: 'Common_BlockFieldLink',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'blockId',
            refObjectType: 'Common_Block',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'fieldId',
            refObjectType: 'Common_Field',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'subsoilUser',
            refObjectType: 'Common_Organization',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'wellPurposeId',
            refObjectType: 'Common_WellPurpose',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'fluidTypeId',
            refObjectType: 'Common_FluidType',
          },
        ],
      },
      {
        maxElements: 1,
        fieldId: 'systemSettings',
        items: [
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'padReadiness',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'padDocumentationReadiness',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'routeReadiness',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'routeDocumentationReadiness',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'electricityReadiness',
          },
          {
            type: 'Boolean',
            control: 'VisibleRadioButton',
            fieldId: 'routeType',
          },
        ],
      },
    ],
  };

  return { ...settings, colorTab: newSettings };
};

export class RigsChartView {
  @observable view: TRigsChartViewWithProcessedSettings;
  /** Rig/well/pad attributes display settings. */
  @observable infoSettingsValues: ViewSettings.InfoSettingsValues = null!;
  /** Settings that not depend on view object. */
  @observable ownSettingsValues: ViewSettings.RigsOwnSettingsValues = null!;

  constructor(view: RigsChartViewTypes.RigsChartView, settingsValues: ViewSettings.RigsChartRawSettingsValues) {
    this.view = this.processViewSettings(view);

    this.setSettingsValues(settingsValues);

    makeObservable(this);
  }

  private processViewSettings(view: RigsChartViewTypes.RigsChartView): TRigsChartViewWithProcessedSettings {
    const keys = Object.keys(view.settings).sort(
      (first, second) => (view.settings[first]?.order ?? 0) - (view.settings[second]?.order ?? 0),
    );

    let processedSettings = keys.reduce((settings: TRigsChartViewWithProcessedSettings['settings'], settingKey) => {
      settings[settingKey] = ViewSettingsHelper.getTypedSetting(view.settings[settingKey]);

      return settings;
    }, {});

    processedSettings = injectAdditionalPadSettings(processedSettings);
    processedSettings = injectAdditionalWellSettings(processedSettings);
    processedSettings = injectAdditionalColorSettings(processedSettings);

    return {
      ...view,
      settings: processedSettings,
    };
  }

  @action
  setSettingsValues = (settingsValues: ViewSettings.RigsChartRawSettingsValues): void => {
    const infoSettingsValues = (() => {
      if (settingsValues.info && ViewSettingsHelper.checkSettingsValues(settingsValues.info)) {
        return { ...DEFAULT_INFO_SETTINGS_VALUE, ...settingsValues.info };
      }

      return DEFAULT_INFO_SETTINGS_VALUE;
    })();

    this.infoSettingsValues = infoSettingsValues;

    this.ownSettingsValues = {
      rigsOrder: settingsValues.rigsOrder,
    };
  };

  @action.bound
  setView(view: RigsChartViewTypes.RigsChartView): void {
    this.view = this.processViewSettings(view);
  }

  @computed
  get settings(): ViewSettings.TypedSettingsControls {
    return this.view.settings;
  }

  @computed
  get shownWellAttributesNumber(): number {
    const wellsSettings = this.infoSettingsValues['wellTab'];

    if (!wellsSettings) {
      return 0;
    }

    if (wellsSettings.type === ViewSettings.ViewSettingsTabsDataTypes.flat) {
      return wellsSettings?.settings.reduce((shownAttributesNumber, currentAttribute) => {
        return shownAttributesNumber + Number(currentAttribute.isShown);
      }, 0);
    } else {
      return wellsSettings?.settings.reduce((shownAttributesNumber, group) => {
        return (
          shownAttributesNumber +
          group.items.reduce((shownAttributesNumber, currentAttribute) => {
            return shownAttributesNumber + Number(currentAttribute.isShown);
          }, 0)
        );
      }, 0);
    }
  }
}
